import { Injectable, inject } from '@angular/core';
import { Observable, map, catchError, of } from 'rxjs';
import { API_ENDPOINTS } from '../core/constants/api-endpoints';
import { appendQuery, getImageUrl } from '../shared/utils/commenFunctions';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

export interface Event {
  id: string;
  title: string;
  description: string;
  date: string; // e.g., "12 December"
  dateTime?: string; // e.g., "12 December, 7:00 pm - 8:00 pm"
  image: string;
  tags?: string[];
  where?: string; // e.g., "BakerRipley Gulfton Sharpstown Center"
  location?: string;
  address?: string;
  mapImage?: string;
  partnership?: string;
  lat?: number;
  lon?: number;
  isRegistered?: boolean;
  isWaitlisted?: boolean;
  registrationStatus?: string | null;
  limitedCapacity?: number | null;
  registeredCount?: number;
  waitlistedCount?: number;
  spotsRemaining?: number | null;
}

interface ApiEventResponse {
  statusCode: number;
  message: string;
  total: number;
  events: ApiEvent[];
}

interface ApiEventDetailResponse {
  statusCode: number;
  message: string;
  event: ApiEvent;
}

export interface ApiEventRegistrationResponse {
  statusCode: number;
  message: string;
  registrationId?: string;
  eventId: string;
  userId: number;
  isRegistered: boolean;
  isWaitlisted?: boolean;
  status?: string;
}

interface ApiEvent {
  id: string;
  eventName: string;
  eventDescription: string;
  eventGroup: string;
  isIndoor: boolean;
  isInperson: boolean;
  eventImage: string;
  eventDate: string;
  eventTime: string;
  when: string;
  where: string;
  location: string;
  lat?: number;
  lon?: number;
  partnership?: string;
  isRegistered?: boolean;
  isWaitlisted?: boolean;
  registrationStatus?: string | null;
  limitedCapacity?: number | null;
  registeredCount?: number;
  waitlistedCount?: number;
  spotsRemaining?: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class EventService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.host;
  private events: Event[] = [];
  private selectedEventId: string | null = null;

  /**
   * Fetches upcoming events from the API
   * @param index Starting index for pagination (default: 0)
   * @param limit Number of events to fetch (default: 10)
   * @param userId Optional user ID to include registration status
   * @returns Observable containing the list of events
   */
  getUpcomingEvents(index: number = 0, limit: number = 10, userId?: number): Observable<Event[]> {
    const payload: Record<string, number> = { index, limit };
    if (userId && userId > 0) {
      payload['userid'] = userId;
    }
    const url = appendQuery(
      `${this.baseUrl}${API_ENDPOINTS.BASE_PATH}${API_ENDPOINTS.EVENTS.UPCOMING_EVENTS}`,
      payload
    );

    return this.http.get<ApiEventResponse>(url).pipe(
      map((response) => {
        if (response.events && Array.isArray(response.events)) {
          this.events = response.events.map((apiEvent) => this.mapApiEventToEvent(apiEvent));
          return this.events;
        }
        return [];
      })
    );
  }

  /**
   * Maps API event response to Event interface
   */
  private mapApiEventToEvent(apiEvent: ApiEvent): Event {
    const tags: string[] = [];
    if (apiEvent.eventGroup) {
      tags.push(apiEvent.eventGroup);
    }
    // Only show these when explicitly true — do not invent In Person / Virtual defaults
    if (apiEvent.isInperson) {
      tags.push('In Person');
    }
    if (apiEvent.isIndoor) {
      tags.push('Indoor');
    } else if (apiEvent.isIndoor === false) {
      tags.push('Outdoor');
    }

    // Build dateTime string from when and eventTime (skip "All day" — date alone is enough)
    let dateTime: string | undefined;
    const whenText = (apiEvent.when || '').trim();
    const eventTime = (apiEvent.eventTime || '').trim();
    const isAllDay = /^all\s*day$/i.test(eventTime);

    if (whenText && eventTime && !isAllDay) {
      dateTime = `${whenText}, ${eventTime}`;
    } else if (whenText) {
      dateTime = whenText;
    } else if (eventTime && !isAllDay) {
      dateTime = eventTime;
    }

    const registrationStatus = apiEvent.registrationStatus ?? null;
    const isWaitlisted =
      apiEvent.isWaitlisted === true || registrationStatus === 'waitlisted';
    const isRegistered =
      apiEvent.isRegistered === true || registrationStatus === 'registered';

    return {
      id: apiEvent.id,
      title: apiEvent.eventName,
      description: apiEvent.eventDescription,
      date: apiEvent.when || apiEvent.eventDate,
      dateTime: dateTime,
      image: getImageUrl(apiEvent.eventImage),
      tags: tags,
      where: apiEvent.where,
      location: apiEvent.where,
      address: apiEvent.location,
      partnership: apiEvent.partnership,
      lat: apiEvent.lat,
      lon: apiEvent.lon,
      isRegistered,
      isWaitlisted,
      registrationStatus,
      limitedCapacity: apiEvent.limitedCapacity ?? null,
      registeredCount: apiEvent.registeredCount ?? 0,
      waitlistedCount: apiEvent.waitlistedCount ?? 0,
      spotsRemaining: apiEvent.spotsRemaining ?? null,
    };
  }

  /**
   * Fetches a single event by ID from the API
   * @param id Event ID
   * @param userId Optional user ID to include registration status
   * @returns Observable containing the event
   */
  getEventById(id: string, userId?: number): Observable<Event | null> {
    const payload: Record<string, number> = {};
    if (userId && userId > 0) {
      payload['userid'] = userId;
    }
    const url = appendQuery(
      `${this.baseUrl}${API_ENDPOINTS.BASE_PATH}${API_ENDPOINTS.EVENTS.DETAIL(id)}`,
      payload
    );

    return this.http.get<ApiEventDetailResponse>(url).pipe(
      map((response) => {
        if (response.event) {
          return this.mapApiEventToEvent(response.event);
        }
        return null;
      })
    );
  }

  /**
   * Register a user for an event
   */
  registerForEvent(eventId: string, userId: number): Observable<ApiEventRegistrationResponse | null> {
    const url = `${this.baseUrl}${API_ENDPOINTS.BASE_PATH}${API_ENDPOINTS.EVENTS.REGISTER}`;
    return this.http.post<ApiEventRegistrationResponse>(url, { eventId, userId }).pipe(
      map((response) => {
        this.updateCachedRegistration(
          eventId,
          response?.isRegistered === true,
          response?.isWaitlisted === true
        );
        return response;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Unregister a user from an event
   */
  unregisterFromEvent(eventId: string, userId: number): Observable<ApiEventRegistrationResponse | null> {
    const url = `${this.baseUrl}${API_ENDPOINTS.BASE_PATH}${API_ENDPOINTS.EVENTS.UNREGISTER}`;
    return this.http.post<ApiEventRegistrationResponse>(url, { eventId, userId }).pipe(
      map((response) => {
        this.updateCachedRegistration(eventId, false, false);
        return response;
      }),
      catchError(() => of(null))
    );
  }

  private updateCachedRegistration(
    eventId: string,
    isRegistered: boolean,
    isWaitlisted: boolean
  ): void {
    const cached = this.events.find((event) => event.id === eventId);
    if (cached) {
      cached.isRegistered = isRegistered;
      cached.isWaitlisted = isWaitlisted;
      cached.registrationStatus = isRegistered
        ? 'registered'
        : isWaitlisted
          ? 'waitlisted'
          : null;
    }
  }

  /**
   * Gets event from cached events array (for backward compatibility)
   * @param id Event ID
   * @returns Event or undefined
   */
  getEventByIdFromCache(id: string): Event | undefined {
    return this.events.find((event) => event.id === id);
  }

  /**
   * Sets the selected event ID
   * @param eventId Event ID to select
   */
  setSelectedEvent(eventId: string): void {
    this.selectedEventId = eventId;
  }

  /**
   * Gets the selected event ID
   * @returns Selected event ID or null
   */
  getSelectedEvent(): string | null {
    return this.selectedEventId;
  }
}
